/**
 * AI concierge for the SearchPage — the pure client half of the feature.
 *
 * The server (`server/api/ai-concierge.js`) turns free text into a structured
 * intent via Claude; this module turns that intent into the same URL search
 * params the filter UI writes, so the existing pipeline (Routes → loadData →
 * listings.query) does everything else. All the codebase-specific guard rails
 * live here, unit-tested in aiConcierge.test.js:
 *
 *  - `seats` only reaches the API alongside `dates` (SearchPage.duck.js) — a
 *    seats param without dates is a filter chip that does nothing, so drop it.
 *  - Lounge searches zero-result on dates/seats (no availability plans), so
 *    those are cleared for them. Both couplings hold only once the intent is
 *    merged over the page's current filters — see normalizeSearchParams.
 *  - `sort=-price` is cheapest-first (configSearch.js maps it to
 *    SortBy.lowestPrice marketplace-wide, and it is not hosted-overridable),
 *    which is the useful default for a lounge search.
 *  - `address` is display-only, `bounds` drives the query — emit both.
 *  - The known cities resolve to bounds without geocoding via
 *    cityLandingPageConfig; anywhere else is handed back for a client-side
 *    Mapbox geocode (`geocodeLocationName`).
 */

import { LOUNGE_MATCHER } from './airportLoungeConfig';
import { CITY_LANDING_CONFIG, CITY_LANDING_SLUGS } from './cityLandingPageConfig';
import { LISTING_TYPE_PARAM, availableRentalDurations } from './searchQuickFilters';

/**
 * Build-time kill switch: the bar renders only where the stage opts in.
 * Like REACT_APP_CSP this is baked into the bundle — flipping it on Heroku
 * needs a redeploy, not a restart.
 */
export const isAiConciergeEnabled = () => process.env.REACT_APP_AI_CONCIERGE === 'true';

/**
 * Runtime availability, as opposed to the build-time flag above.
 *
 * A stage can ship the bar and still have no working endpoint: without
 * ANTHROPIC_API_KEY, or once the daily call cap trips mid-day, the server
 * answers 503 and every submit degrades to a raw keyword search. That state is
 * documented as supported — but the Topbar suppresses its own location search
 * wherever the concierge renders, and on a `searchType: 'location'` marketplace
 * a keyword-only concierge plus no topbar form leaves the visitor unable to
 * enter a place at all. So the first 503 is remembered and broadcast, and the
 * Topbar brings its search back.
 *
 * Module state, not Redux: this is a single boolean that only ever flips one
 * way, and it must be readable from both the concierge and the Topbar without
 * coupling them. The event is what makes an already-mounted Topbar re-render.
 */
export const AI_CONCIERGE_UNAVAILABLE_EVENT = 'aiConciergeUnavailable';

let conciergeUnavailable = false;

export const isAiConciergeUnavailable = () => conciergeUnavailable;

export const markAiConciergeUnavailable = () => {
  if (conciergeUnavailable) {
    return;
  }
  conciergeUnavailable = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AI_CONCIERGE_UNAVAILABLE_EVENT));
  }
};

/** Test seam — the flag is module state that would otherwise leak across cases. */
export const resetAiConciergeAvailability = () => {
  conciergeUnavailable = false;
};

/** Today in the marketplace's time zone, as YYYY-MM-DD (sv-SE formats ISO-style). */
export const localTodayISO = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Zurich' });

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// The shape check alone lets impossible dates through — "2026-02-30" is a
// classic relative-date arithmetic slip. Such a date reaches the URL and the
// reply sentence, but the duck's moment() parse rejects it and the query then
// runs with no date filter at all: the concierge claims a date it never
// applied. Round-trip through Date to keep only real calendar days.
const isRealDate = value => {
  if (!ISO_DATE.test(`${value}`)) {
    return false;
  }
  const [year, month, day] = value.split('-').map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
};

/**
 * The request body for POST /api/ai-concierge: the free text plus the
 * vocabulary the schema enums are built from. Categories come from the hosted
 * tree (top level — the quick filters only ever set categoryLevel1 too), the
 * city slugs from the repo's landing page config.
 *
 * @param {string} text free-text request
 * @param {Object} config marketplace config (useConfiguration)
 * @param {string} locale two-letter UI locale
 * @returns {Object} JSON body for util/api.js aiConciergeParse
 */
export const conciergeRequestPayload = (text, config, locale) => {
  const categories = config?.categoryConfiguration?.categories || [];
  return {
    text,
    locale,
    categories: categories.map(c => ({ id: c.id, name: c.name })),
    citySlugs: CITY_LANDING_SLUGS,
  };
};

// Safety net when the model set a citySlug but no readable place name.
const cityLabelFromSlug = slug =>
  `${slug || ''}`
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

/**
 * Maps a parsed intent to SearchPage URL search params.
 *
 * @param {Object} intent parsed intent from the server endpoint
 * @param {Array<Object>} listingTypes `config.listing.listingTypes`
 * @param {string} [todayISO] injectable for tests
 * @returns {{searchParams: Object, geocodeLocationName: string|null}}
 *   `searchParams` is ready for createResourceLocatorString('SearchPage', …);
 *   `geocodeLocationName` is set when the place still needs a Mapbox forward
 *   geocode (unknown city) — on failure the caller merges it into `keywords`.
 */
export const intentToSearchParams = (intent, listingTypes = [], todayISO = localTodayISO()) => {
  const {
    citySlug,
    locationName,
    dateStart,
    dateEnd,
    seats,
    rentalDuration,
    categoryId,
    priceMaxChf,
    keywords,
  } = intent || {};

  const searchParams = {};
  const isLounge = !!categoryId && LOUNGE_MATCHER.test(categoryId);

  if (categoryId) {
    searchParams.pub_categoryLevel1 = categoryId;
  }

  const cityConfig = citySlug ? CITY_LANDING_CONFIG[citySlug] : null;
  let geocodeLocationName = null;
  if (cityConfig) {
    searchParams.bounds = cityConfig.boundsStr;
    searchParams.address = locationName || cityLabelFromSlug(citySlug);
  } else if (locationName) {
    geocodeLocationName = locationName;
  }

  const start = isRealDate(dateStart) ? dateStart : null;
  const end = start && isRealDate(dateEnd) && dateEnd >= start ? dateEnd : start;
  // Past dates are dropped entirely: the duck would ignore them for the query
  // but the URL would still show a red "invalid dates" notice.
  if (!isLounge && start && start >= todayISO) {
    searchParams.dates = `${start},${end}`;
  }
  // Emitted whenever the visitor stated one, NOT gated on this intent's own
  // dates: the merge may supply dates from the current URL, which makes the
  // seat count meaningful after all. normalizeSearchParams drops it against
  // the merged params if no dates survive there.
  if (Number.isInteger(seats) && seats >= 1) {
    searchParams.seats = seats;
  }

  if (rentalDuration) {
    const duration = availableRentalDurations(listingTypes).find(d => d.key === rentalDuration);
    if (duration) {
      searchParams[LISTING_TYPE_PARAM] = duration.listingTypeIds.join(',');
    }
  }

  if (Number.isInteger(priceMaxChf) && priceMaxChf > 0) {
    // URL format is "min,max" in main currency units (the duck converts to subunits).
    searchParams.price = `0,${priceMaxChf}`;
  }

  if (typeof keywords === 'string' && keywords.trim().length > 0) {
    searchParams.keywords = keywords.trim();
  }

  if (isLounge) {
    searchParams.sort = '-price';
  }

  return { searchParams, geocodeLocationName };
};

/**
 * Enforce the two SearchPage couplings that can only be judged once the
 * intent's params sit on top of the page's current ones:
 *
 *  - A lounge search zero-results on `dates`/`seats` (the imported lounges
 *    carry no availability plans), so SearchPage.duck.js strips them from the
 *    query. Left in the URL they still render a date chip AND trip the red
 *    "invalid dates" notice over results that are perfectly fine, because the
 *    lounge branch removes `dates` from the filter config it validates
 *    against. Clearing them here keeps URL and query telling the same story.
 *  - `seats` only reaches the API alongside `dates`.
 *
 * Both must run against the MERGED params: a `pub_categoryLevel1` carried over
 * from the current URL can make a search a lounge search that the intent alone
 * never looked like, and carried `dates` can make an intent's seat count
 * meaningful after all.
 *
 * @param {Object} params merged search params
 * @returns {Object} new params object
 */
export const normalizeSearchParams = params => {
  const next = { ...params };
  if (LOUNGE_MATCHER.test(`${next.pub_categoryLevel1 || ''}`)) {
    delete next.dates;
    delete next.seats;
  }
  if (!next.dates) {
    delete next.seats;
  }
  return next;
};

/**
 * Fold an ungeocodable place into the keyword search so the location is not
 * silently lost — "Lugano Bahnhof" still narrows the results as text.
 *
 * @param {Object} searchParams params from intentToSearchParams
 * @param {string} locationName the place that could not be geocoded
 * @returns {Object} new params object
 */
export const mergeLocationIntoKeywords = (searchParams, locationName) => {
  const keywords = [locationName, searchParams.keywords].filter(Boolean).join(' ');
  return { ...searchParams, keywords };
};

// ================ Free-text hygiene (SearchAiConcierge + the unified search Wo field) ================ //

// Slicing cuts by UTF-16 code unit, so a cap landing inside an astral
// character (emoji) leaves a lone surrogate — ill-formed Unicode by the time
// it reaches the API. Drop a trailing high surrogate so the cut lands on a
// whole character. Mirrors sliceWholeChars in server/api/ai-concierge.js.
export const sliceWholeChars = (value, max) => {
  const cut = `${value || ''}`.slice(0, max);
  const lastCode = cut.charCodeAt(cut.length - 1);
  return lastCode >= 0xd800 && lastCode <= 0xdbff ? cut.slice(0, -1) : cut;
};

// Best-effort scrub of incidental PII, which a free-text box invites far more
// than a plain keyword search does. Not exhaustive — names aren't reliably
// detectable — but it covers the two most identifiable, cheaply-detectable
// shapes.
//
// Applied to BOTH destinations the text reaches — the analytics event and the
// `keywords` URL param. Scrubbing only the analytics field achieved nothing:
// the raw text still landed in the URL, and SearchPage.duck.js reported that
// to GA4 and the Reddit pixel on the very next navigation.
//
// Phone matching works by exclusion rather than by enumerating dial plans. A
// broad digit-run catches every national and international shape; the two
// things that must survive it are dates and decimal prices. That is what an
// earlier catch-all destroyed, and what the narrow per-country patterns which
// replaced it under-matched (they leaked 0041…, 0800…, and every non-Swiss
// format).
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DECIMAL_ONLY = /^\d+[.,]\d{1,2}$/;
const DATE_LIKE = /\d{4}-\d{2}-\d{2}|\b\d{1,2}[./]\d{1,2}[./]\d{2,4}\b/g;
const PHONE_CANDIDATE = /\+?\d[\d\s()./-]{6,}\d/g;

const isDateOrPrice = match => {
  const trimmed = match.trim();
  if (ISO_DATE_ONLY.test(trimmed) || DECIMAL_ONLY.test(trimmed)) {
    return true;
  }
  // A range such as "2026-09-01 - 2026-09-05" or "01.09.2026 - 05.09.2026":
  // strip the dates and only separators are left.
  return !/\d/.test(trimmed.replace(DATE_LIKE, ''));
};

export const redactPii = value =>
  `${value || ''}`
    // Unicode-aware: the previous \w-based pattern left the non-ASCII part of
    // an address behind ("max.müller@…" became "max.mü[email]").
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/gu, '[email]')
    .replace(PHONE_CANDIDATE, match =>
      !isDateOrPrice(match) && match.replace(/\D/g, '').length >= 7 ? '[number]' : match
    );
